/**
 * MobileShell — the app frame for phones.
 *
 * Replaces <Layout> on phone-sized viewports for workshop staff AND for fleet
 * customers. Layout is built around a 16rem sidebar and a hamburger drawer listing
 * every page in the product; on a phone that is both cramped and wrong, because the
 * phone build deliberately carries a handful of screens (see App.jsx).
 *
 * Two audiences, one shell: workshop staff get Jobs + Booking, fleet customers get
 * Bookings + Invoices, and both get Me. Which set appears is decided entirely by the
 * uiViews claim — see TABS below.
 *
 * Structure, top to bottom:
 *   • connection / update banners  — always visible, never scroll away
 *   • the active screen            — owns its own scrolling
 *   • bottom tab bar               — thumb-reachable, the only navigation
 *
 * The frame is `fixed inset-0` rather than a tall document so the page itself
 * never scrolls. That keeps the tab bar planted while a long job list scrolls
 * underneath it, and stops iOS Safari's rubber-band from dragging the whole UI
 * away from the bottom edge.
 *
 * There is no top bar. Vertical space on a 375×667 phone is the scarcest
 * resource on this screen, and everything a top bar would have carried
 * (identity, sign out, help) lives on the Me tab instead.
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Bell, CalendarPlus, FileText, User, Wrench, FileSignature } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { selectUser } from '../../store/slices/authSlice';
import { selectUnreadCount } from '../../store/slices/notificationSlice';
import useNotificationSync from '../../hooks/useNotificationSync';
import { UiViewKey } from '../../constants/uiViewKeys';
import useServiceWorkerUpdate from '../../pwa/useServiceWorkerUpdate';
import { OfflineBanner, UpdateBanner } from '../pwa/PwaBanners';

/*
 * Tabs are declared in display order and filtered against the user's uiViews claim
 * AND their audience. The Me tab has neither — it is shell chrome (sign out has to be
 * reachable), not a product view, so it is always present.
 *
 * ⚠️ `audience` exists because the claim alone is NOT enough, which is what this
 * originally assumed. The old reasoning was that a workshop user never holds FLEET_*
 * and a fleet CLIENT never holds JOBS — but a STAFF member's uiViews are whatever is
 * ticked on their UiViewProfile, and the server applies no scope filter to it. A
 * profile with the fleet keys ticked produced EIGHT tabs on a phone, with Bookings and
 * Invoices appearing twice and the second of each leading to the customer portal.
 *
 * Staff identity is the reliable discriminator and matches how the server actually
 * works: fleet portal endpoints scope by customerUuid, which a staff session does not
 * have, so those screens could never load for them anyway.
 *
 * Fleet ordering puts Bookings first: a fleet customer opens this to see what is
 * booked in, not to read a bill.
 */
/** Who a tab is for. 'workshop' needs a staff identity; 'fleet' needs the absence of one. */
const WORKSHOP = 'workshop';
const FLEET = 'fleet';
const TABS = [
    // Workshop staff
    { key: UiViewKey.JOBS,           audience: WORKSHOP, path: '/jobs',           label: 'Jobs',     icon: Wrench        },
    { key: UiViewKey.BOOKINGS,       audience: WORKSHOP, path: '/bookings',       label: 'Bookings', icon: CalendarPlus  },
    { key: UiViewKey.QUOTES,         audience: WORKSHOP, path: '/quotes',         label: 'Quotes',   icon: FileSignature },
    { key: UiViewKey.INVOICES,       audience: WORKSHOP, path: '/invoices',       label: 'Invoices', icon: FileText      },
    // Fleet customers (CLIENT logins)
    { key: UiViewKey.FLEET_BOOKINGS, audience: FLEET,    path: '/fleet/bookings', label: 'Bookings', icon: CalendarPlus  },
    { key: UiViewKey.FLEET_INVOICES, audience: FLEET,    path: '/fleet/invoices', label: 'Invoices', icon: FileText      },
    // Shell chrome
    // Notifications belong to a STAFF member — the server reads the recipient from the
    // staffUuid claim — so this carries the workshop audience and no view key.
    { key: null,                     audience: WORKSHOP, path: '/notifications',  label: 'Alerts',   icon: Bell          },
    { key: null,                     audience: null,     path: '/me',             label: 'Me',       icon: User          },
];

/**
 * The tabs a given session can see, in display order.
 *
 * A session is workshop staff when it has a staffUuid, and a fleet customer when it
 * does not. Both filters apply: the audience decides which SET of tabs is on offer,
 * the uiViews claim decides which of them this person may see.
 */
export const mobileTabsFor = (uiViews, staffUuid = null) => {
    const allowed = new Set(uiViews || []);
    const isStaff = !!staffUuid;
    return TABS.filter((t) => {
        if (t.audience === WORKSHOP && !isStaff) return false;
        if (t.audience === FLEET && isStaff) return false;
        return t.key === null || allowed.has(t.key);
    });
};

const MobileShell = ({ children }) => {
    const user = useAppSelector(selectUser);
    const location = useLocation();
    const { updateReady, applyUpdate, dismissUpdate } = useServiceWorkerUpdate();

    const tabs = mobileTabsFor(user?.uiViews, user?.staffUuid);
    const unreadCount = useAppSelector(selectUnreadCount);

    // Keeps the badge honest while the app is open — see the hook.
    useNotificationSync(!!user?.staffUuid);

    return (
        <div className="mobile-shell fixed inset-0 flex flex-col bg-gray-50 overflow-hidden">
            {/* Banners sit above everything, inside the top safe-area inset so
                they clear the notch in an installed app. */}
            <div className="pt-safe shrink-0">
                <OfflineBanner />
                {updateReady && (
                    <UpdateBanner onApply={applyUpdate} onDismiss={dismissUpdate} />
                )}
            </div>

            {/* Screens are flex children, not a scroll container — each one
                manages its own scrolling region so headers can stay pinned. */}
            <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {children}
            </main>

            {/* Bottom tab bar. `pb-safe` on the container rather than the links
                so the inset is dead space below the tap targets, not padding
                inside them — otherwise the labels drift upward on a notched
                phone and the row loses its optical centre. */}
            <nav className="pb-safe bg-white border-t border-gray-200 shrink-0">
                <div className="flex">
                    {tabs.map(({ path, label, icon: Icon }) => {
                        const active = location.pathname === path;
                        const badge = path === '/notifications' ? unreadCount : 0;
                        return (
                            <NavLink
                                key={path}
                                to={path}
                                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[52px]
                                    ${active ? 'text-emerald-600' : 'text-gray-400'}`}
                            >
                                <span className="relative">
                                    <Icon size={20} strokeWidth={active ? 2.4 : 2} />
                                    {/* Capped at 9+. The tab is 62px wide on a 375px
                                        phone and a three-digit count overflows it. */}
                                    {badge > 0 && (
                                        <span aria-label={`${badge} unread`}
                                              className="absolute -right-2 -top-1 min-w-[16px] rounded-full bg-red-500
                                                         px-1 text-[10px] font-bold leading-4 text-white">
                                            {badge > 9 ? '9+' : badge}
                                        </span>
                                    )}
                                </span>
                                <span className={`text-[10px] leading-none ${active ? 'font-bold' : 'font-medium'}`}>
                                    {label}
                                </span>
                            </NavLink>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
};

export default MobileShell;
